
import { Component, Vue } from 'vue-property-decorator'
import moment from 'moment'
import { listUserOtaOverrides, clearUserOtaOverride, bulkClearUserOtaOverrides } from '@/api/userOtaOverride'
import request from '@/utils/request'

interface PinRow {
  userId: string
  deviceSerialNumber: string | null
  otaFileId: number
  reason: string | null
  setBy: string | null
  updatedAt: string
  otaFile: any
  user: any
}

@Component({ name: 'PinnedDevicesPanel' })
export default class extends Vue {
  private rows: PinRow[] = []
  private otaFiles: any[] = []
  private selected: PinRow[] = []
  private loading = false
  private clearing = false

  private search = ''
  private deviceSerial = ''
  private otaFileId: number | '' = ''
  // Scope has no server-side filter, so it narrows the loaded page instead of
  // the query. Called out in the UI so a filtered count is not mistaken for a
  // total across all pages.
  private scope = ''

  private page = 1
  private pageSize = 50
  private total = 0

  created() {
    this.fetchOtaFiles()
    this.fetchPins()
  }

  private get visibleRows(): PinRow[] {
    if (this.scope === 'user') return this.rows.filter(r => !r.deviceSerialNumber)
    if (this.scope === 'device') return this.rows.filter(r => !!r.deviceSerialNumber)
    return this.rows
  }

  private buildLabel(f: any): string {
    return `${f.applicationVersionName} / ${f.wirelessVersionName}${f.isPinOnly ? ' (pin-only)' : ''}`
  }

  private formatDate(v: string) {
    return v ? moment(v).format('YYYY-MM-DD HH:mm') : '-'
  }

  private async fetchOtaFiles() {
    try {
      // Same endpoint the OTA Files screen uses; there is no dedicated api
      // module for it, so call it directly rather than inventing one.
      const res: any = await request.get('/otafiles/all', { timeout: 30000 })
      this.otaFiles = res.data?.data || res.data || []
    } catch {
      this.otaFiles = []
    }
  }

  private async fetchPins() {
    this.loading = true
    try {
      const params: any = { page: this.page, pageSize: this.pageSize }
      if (this.search.trim()) params.search = this.search.trim()
      if (this.deviceSerial.trim()) params.deviceSerial = this.deviceSerial.trim()
      if (this.otaFileId) params.otaFileId = this.otaFileId
      const res: any = await listUserOtaOverrides(params)
      this.rows = res.data?.data || []
      this.total = res.data?.pagination?.totalCount || 0
      this.selected = []
    } catch {
      this.$message.error('Failed to load pins')
      this.rows = []
      this.total = 0
    } finally {
      this.loading = false
    }
  }

  private handleSearch() {
    this.page = 1
    this.fetchPins()
  }

  private handleReset() {
    this.search = ''
    this.deviceSerial = ''
    this.otaFileId = ''
    this.scope = ''
    this.page = 1
    this.fetchPins()
  }

  private handlePageChange(p: number) {
    this.page = p
    this.fetchPins()
  }

  private handlePageSizeChange(size: number) {
    this.pageSize = size
    this.page = 1
    this.fetchPins()
  }

  private handleSelectAllOnPage() {
    const table: any = this.$refs.pinTable
    if (!table) return
    table.clearSelection()
    this.visibleRows.forEach((row: PinRow) => table.toggleRowSelection(row, true))
  }

  private async handleClearOne(row: PinRow) {
    try {
      await this.$confirm(
        row.deviceSerialNumber
          ? `Remove the pin on cane ${row.deviceSerialNumber}?`
          : 'Remove the account-wide pin for this user?',
        'Warning',
        { confirmButtonText: 'Remove', cancelButtonText: 'Cancel', type: 'warning' }
      )
      await clearUserOtaOverride(
        row.userId,
        row.deviceSerialNumber || undefined,
        !row.deviceSerialNumber
      )
      this.$message.success('Pin removed')
      this.fetchPins()
    } catch {
      // cancelled
    }
  }

  private async handleBulkClear() {
    if (!this.selected.length) return
    try {
      await this.$confirm(
        `Remove ${this.selected.length} pin(s)? Affected users fall back to the normal rollout on their next check.`,
        'Warning',
        { confirmButtonText: 'Remove', cancelButtonText: 'Cancel', type: 'warning' }
      )
      this.clearing = true
      const res: any = await bulkClearUserOtaOverrides(
        this.selected.map(r => ({
          userId: r.userId,
          deviceSerialNumber: r.deviceSerialNumber
        }))
      )
      this.$message.success(`${res.data?.data?.deleted ?? this.selected.length} pin(s) removed`)
      this.fetchPins()
    } catch {
      // cancelled
    } finally {
      this.clearing = false
    }
  }
}
